// Sidebar.js
import React, { useEffect, useRef, useMemo } from 'react';
import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Toolbar,
  useTheme,
} from '@mui/material';
import {
  Home,
  Briefcase,
  MessageCircle,
  BookOpen,
  Info,
  ChevronLeft,
  ChevronRight,
} from 'react-feather';
import ElephantImage from '../assets/images/Sidebar_elephant.png';
import analyticsService from '../services/analytics';

function Sidebar({ currentTab, onTabChange, isOpen, setIsOpen }) {
  const theme = useTheme();
  // Ref to store the current tab tracking info
  const activeTabRef = useRef(null);
  
  // Store last tracking timestamp to prevent duplicate events
  const lastTrackingTimestampRef = useRef(null);

  // Icon size that scales with viewport - will be smaller at high zoom levels
  const iconSize = 20; // Default size, will scale with container padding

  // Use useMemo to prevent menuItems from being recreated on every render
  const menuItems = useMemo(() => [
    { label: 'Top Grants', icon: <Home size={iconSize} color={theme.palette.text.secondary} /> },
    { label: 'My Portfolio', icon: <Briefcase size={iconSize} color={theme.palette.text.secondary} /> },
    { label: 'Chat', icon: <MessageCircle size={iconSize} color={theme.palette.text.secondary} /> },
    { label: 'UA Resources', icon: <BookOpen size={iconSize} color={theme.palette.text.secondary} /> },
    { label: 'About', icon: <Info size={iconSize} color={theme.palette.text.secondary} /> },
  ], [theme.palette.text.secondary]); // Only re-create when the theme color changes

  // Handle tab change and track analytics
  const handleItemClick = (index) => {
    // Don't track if clicking the same tab
    if (currentTab === index) {
      return;
    }
    
    // Get the current time to prevent duplicate events
    const now = new Date();
    
    // If there's an active tab being tracked, end it
    if (activeTabRef.current) {
      const { tabViewId, tabName, startTime } = activeTabRef.current;
      
      // Calculate duration in seconds with minimum of 1s
      const durationSeconds = Math.max(1, Math.round((now - startTime) / 1000));
      
      // Validate parameters before sending
      if (tabViewId && tabName && durationSeconds > 0) {
        // Use then instead of an async function to ensure this completes before starting the new tab tracking
        analyticsService.trackTabEndedSync(tabViewId, tabName, durationSeconds)
          .catch(err => {
            console.warn('Error ending tab tracking:', err);
          });
      }
    }
    
    // Start tracking the new tab - safe access to menuItems with array bounds check
    // eslint-disable-next-line security/detect-object-injection
    const newTabItem = index >= 0 && index < menuItems.length ? menuItems[index] : null;
    if (!newTabItem) {
      console.warn(`Invalid menu item index: ${index}`);
      onTabChange(index);
      return;
    }

    const newTabName = newTabItem.label.toLowerCase().replace(/\s+/g, '_');
    
    // Change the tab in the UI first for better responsiveness
    onTabChange(index);
    
    // Prevent duplicate tracking within 1 second
    const isRecentTrack = lastTrackingTimestampRef.current && 
      (now - lastTrackingTimestampRef.current) < 1000;
    
    if (isRecentTrack) {
      // Still update the ref with basic info
      activeTabRef.current = {
        tabName: newTabName,
        startTime: now
      };
      return;
    }
    
    // Update last tracking timestamp
    lastTrackingTimestampRef.current = now;
    
    // Then start tracking the new tab
    analyticsService.trackTabStarted(newTabName)
      .then(({ tabViewId, startTime }) => {
        // Store tracking info in the ref
        activeTabRef.current = {
          tabViewId,
          tabName: newTabName,
          startTime: startTime || now // Fallback if no startTime
        };
      })
      .catch(err => {
        console.warn('Error starting tab tracking:', err);
        // Still store something in the ref so we can track duration
        activeTabRef.current = {
          tabName: newTabName,
          startTime: now
        };
      });
  };

  // Track initial tab view on component mount and handle cleanup
  useEffect(() => {
    // Safely access menuItems with bounds check
    // eslint-disable-next-line security/detect-object-injection
    const initialTabItem = currentTab >= 0 && currentTab < menuItems.length ? menuItems[currentTab] : null;
    if (!initialTabItem) {
      console.warn(`Invalid initial tab index: ${currentTab}`);
      return;
    }

    const initialTabName = initialTabItem.label.toLowerCase().replace(/\s+/g, '_');
    
    // Flag to prevent race conditions
    let isMounted = true;
    
    // Set initial timestamp to prevent duplicate tracking
    lastTrackingTimestampRef.current = new Date();
    
    analyticsService.trackTabStarted(initialTabName)
      .then(({ tabViewId, startTime }) => {
        // Skip if component unmounted before this promise resolved
        if (!isMounted) return;
        
        // Store tracking info in the ref
        activeTabRef.current = {
          tabViewId,
          tabName: initialTabName,
          startTime: startTime || new Date()
        };
      })
      .catch(err => {
        console.warn('Error tracking initial tab:', err);
        // Only set ref if still mounted
        if (isMounted) {
          // Still store basic info so we can track duration
          activeTabRef.current = {
            tabName: initialTabName,
            startTime: new Date()
          };
        }
      });
    
    // Add beforeunload event handler to track tab duration when page unloads
    const handleBeforeUnload = () => {
      if (activeTabRef.current) {
        const { tabViewId, tabName, startTime } = activeTabRef.current;
        if (tabName && startTime) {
          const durationSeconds = Math.max(1, Math.round((new Date() - startTime) / 1000));
          // Use sync version which uses navigator.sendBeacon internally
          analyticsService.trackTabEndedSync(tabViewId, tabName, durationSeconds);
        }
      }
    };
    
    // Add the event listener
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Cleanup on unmount - track the final tab duration
    return () => {
      isMounted = false;
      
      // Remove the event listener
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      if (activeTabRef.current) {
        const { tabViewId, tabName, startTime } = activeTabRef.current;
        
        if (tabName && startTime) {
          // Use Math.max to ensure minimum 1 second duration
          const durationSeconds = Math.max(1, Math.round((new Date() - startTime) / 1000));
          
          // Use sync version to ensure it completes even during page unload
          analyticsService.trackTabEndedSync(tabViewId, tabName, durationSeconds)
            .catch(err => console.warn('Error ending tab on unmount:', err));
        }
      }
    };
  // Include currentTab and menuItems as dependencies, they're used inside
  }, [currentTab, menuItems]);

  // Use rem units and responsive sizing for better zoom accessibility
  // At 100% zoom: full size. At 250% zoom (viewport ~768px): reduced size
  const drawerWidthOpen = { xs: '12rem', sm: '13rem', md: '15rem' }; // 192px -> 208px -> 240px
  const drawerWidthClosed = { xs: '3.5rem', sm: '4rem', md: '4.375rem' }; // 56px -> 64px -> 70px

  return (
    <Drawer
      variant="permanent"
      open={isOpen}
      sx={{
        width: isOpen ? drawerWidthOpen : drawerWidthClosed,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        '& .MuiDrawer-paper': {
          width: isOpen ? drawerWidthOpen : drawerWidthClosed,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter,
          }),
          overflowX: 'hidden',
          boxShadow: 'none',
        },
      }}
    >
      {/* Spacer for the AppBar */}
      <Toolbar />

      {/* Main container: using flex with space-between */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* Top Section: Toggle Button & Menu Items */}
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: isOpen ? 'flex-end' : 'center',
              p: 1,
            }}
          >
            <Tooltip title={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
              <IconButton onClick={() => setIsOpen((prev) => !prev)} size="small">
                {isOpen ? (
                  <ChevronLeft size={iconSize} color={theme.palette.text.secondary} />
                ) : (
                  <ChevronRight size={iconSize} color={theme.palette.text.secondary} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
          <List disablePadding>
            {menuItems.map((item, index) => (
              <ListItemButton
                key={item.label}
                selected={currentTab === index}
                onClick={() => handleItemClick(index)}
                sx={{
                  px: { xs: 1.5, sm: 1.75, md: 2 },
                  py: { xs: 1, sm: 1.25, md: 1.5 },
                  borderRadius: 2,
                  backgroundColor:
                    currentTab === index ? theme.palette.primary.light : 'transparent',
                  '&:hover': {
                    backgroundColor:
                      currentTab === index
                        ? theme.palette.primary.light
                        : theme.palette.action.hover,
                    borderRadius: 2,
                  },
                  '&:focus': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '2px',
                  },
                  '& .MuiListItemText-primary': {
                    fontSize: {
                      xs: currentTab === index ? '0.95rem' : '0.875rem',
                      sm: currentTab === index ? '1rem' : '0.9rem',
                      md: currentTab === index ? '1.11rem' : '1rem',
                    },
                    fontWeight: currentTab === index ? '600' : '400',
                    transition: 'font-size 0.2s ease',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isOpen ? 2 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {isOpen && <ListItemText primary={item.label} />}
              </ListItemButton>
            ))}
          </List>
        </Box>

        {/* Bottom Section: Elephant Image flush with the bottom */}
        <Box
          sx={{
            mt: 'auto',
            width: '100%',
            p: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-end',
          }}
        >
          {isOpen && (
            <img
              src={ElephantImage}
              alt="A solid gray silhouette of an elephant facing left. The image captures the full body profile of the elephant, including its curved trunk, large ears, and distinct legs and tail, with no background."
              style={{ maxWidth: '90%', height: 'auto', marginBottom: 0 }}
            />
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

export default Sidebar;
